// ═══════════════════════════════════════════════════════════════════════
// shared/reserved-slugs.ts — Single source of truth for tenant slug rules.
// ═══════════════════════════════════════════════════════════════════════
// Used by both the build app (client-side slug assignment in auth.tsx and
// preview rendering in IntakePreview.tsx) AND server-side API routes.
//
// IMPORTANT: this list MUST match the subdomain reservations in the Q2
// strategy doc. If you add a service subdomain (e.g. a new ops domain),
// add it here AND to the Traefik routing rules on the substrate.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Reserved subdomains — never assign these as tenant slugs.
 * Lowercased. Order is irrelevant since we lookup via Set.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  // Service / platform
  "admin", "api", "app", "auth", "dash", "dashboard", "docs", "help",
  "login", "logout", "mail", "my", "ops", "signin", "signup", "status",
  "support", "vault", "www", "account",
  // Marketing / brand
  "blog", "send",
  // Substrate / infra
  "brain", "build", "coolify", "inngest", "langfuse", "n8n", "phase1",
  "qdrant", "sense", "sloelabs", "substrate", "tech", "uptime",
  // Product lines
  "fit", "realty", "scout", "scoutbase-api", "sportnaa",
]);

/**
 * Slugify a free-form name into a URL-safe slug fragment.
 * Lowercase, non-alphanumerics → single dash, no leading/trailing dashes.
 *
 * NOTE: This does NOT add a random suffix or check reservation. Use
 * assignSafeSlug() for actual tenant assignment.
 */
export function slugify(name: string): string {
  if (!name) return "";
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Is this slug on the reserved list?
 * Checks the bare slug only — assignSafeSlug appends a random suffix
 * before this is meaningful in practice, but exposed for validation.
 */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug.toLowerCase());
}

/**
 * Generate a 4-char random suffix using base36.
 * ~1.6M possible values; collisions are statistically negligible at
 * tenant scale (<10K active tenants).
 */
function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 6);
}

/**
 * Assign a tenant-safe subdomain slug from a free-form company name.
 *
 * Format: `{slugified-name}-{suffix}` where suffix is 4 random chars.
 *
 * Guarantees:
 *  1. Never returns a bare reserved slug (e.g. "brain", "api").
 *  2. Always appends a random suffix for collision resistance.
 *  3. If name is empty/blank, returns an os-prefixed fallback.
 *  4. If the resulting slug coincidentally matches a reserved word
 *     (extremely unlikely with the suffix, but the suffix could in
 *     theory equal a reserved word — we re-roll the suffix in that case).
 *
 * The output is the FULL slug to write to operating_systems.subdomain_slug.
 */
export function assignSafeSlug(name: string): string {
  // Empty / unusable name: fallback with random suffix only.
  const base = slugify(name);
  if (!base) {
    let candidate = `os-${randomSuffix()}`;
    while (isReservedSlug(candidate)) {
      candidate = `os-${randomSuffix()}`;
    }
    return candidate;
  }

  // If the base itself is reserved (e.g. company literally named "Brain"),
  // we still append a suffix — `brain-x7k2` is safe and not on the list.
  // But re-roll if the final composite is on the list (~zero probability).
  let candidate = `${base}-${randomSuffix()}`;
  let attempts = 0;
  while (isReservedSlug(candidate) && attempts < 10) {
    candidate = `${base}-${randomSuffix()}`;
    attempts++;
  }
  return candidate;
}
